(() => {
  if (customElements.get('mvp-community-slider')) return;
  class MvpCommunitySlider extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.isReels = this.hasAttribute('data-reels');
      if (this.isReels) {
        this.querySelectorAll('[data-cart-link]').forEach(link => link.remove());
        // Also clean selectors rendered by an older copy of the Liquid file.
        this.querySelectorAll('[data-variant] option:disabled').forEach(option => option.remove());
      }
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.reasons = new Set();
      this.playingVideos = new Set();
      this.playSequence = 0;
      this.designMode = this.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);
      this.selected = false;
      this.pendingForms = new WeakSet();
      this.pendingCount = 0;
      this.originals = [...this.track.children];
      this.cloneSets = [];
      this.layoutSignature = null; this.layoutPending = false; this.hoveredCard = null; this.dragState = null; this.lastFrame = 0;
      this.auto = this.isReels && this.dataset.auto === 'true' && !this.reduced.matches && !this.designMode;
      this.cycle = 0;
      this.timer = 0;
      this.ready = false;
      this.pauseButton = this.querySelector('[data-motion]');
      this.querySelector('[data-previous]')?.addEventListener('click', () => this.step(-1), { signal });
      this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1), { signal });
      this.pauseButton?.addEventListener('click', () => {
        this.auto = !this.auto;
        this.syncMotionButton();
      }, { signal });
      this.addEventListener('mouseenter', () => this.reasons.add('hover'), { signal });
      this.addEventListener('mouseleave', () => this.reasons.delete('hover'), { signal });
      this.addEventListener('focusin', event => {
        if (event.target === this.pauseButton) this.reasons.delete('focus');
        else this.reasons.add('focus');
      }, { signal });
      this.addEventListener('focusout', () => requestAnimationFrame(() => {
        if (!this.contains(document.activeElement) || document.activeElement === this.pauseButton) this.reasons.delete('focus');
      }), { signal });
      this.track.addEventListener('pointerdown', event => {
        this.reasons.add('pointer'); this.pauseUntil = performance.now() + 2000;
        if (!this.isReels || this.dataset.mouseDrag !== 'true' || event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('a,button,select,input,textarea,label,[data-buy],.mvp-community__product')) return;
        this.dragState = { id: event.pointerId, x: event.clientX, last: event.clientX, moved: false };
      }, { signal });
      this.track.addEventListener('pointermove', event => {
        const drag = this.dragState;
        if (!drag || drag.id !== event.pointerId) return;
        if (!drag.moved && Math.abs(event.clientX - drag.x) < 6) return;
        if (!drag.moved) { drag.moved = true; this.track.setPointerCapture(event.pointerId); this.track.setAttribute('data-dragging', ''); this.querySelectorAll('video').forEach(v => this.pauseVideo(v)); }
        event.preventDefault(); this.track.scrollLeft += drag.last - event.clientX; drag.last = event.clientX;
      }, { signal });
      const endDrag = event => {
        if (!this.dragState || event.pointerId !== this.dragState.id) return;
        this.suppressClickUntil = this.dragState.moved ? performance.now() + 350 : 0;
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.dragState = null; this.track.removeAttribute('data-dragging');
      };
      window.addEventListener('pointerup', endDrag, { signal });
      window.addEventListener('pointercancel', endDrag, { signal });
      this.track.addEventListener('lostpointercapture', endDrag, { signal });
      this.track.addEventListener('dragstart', event => { if (this.isReels && this.dataset.mouseDrag === 'true') event.preventDefault(); }, { signal });
      this.track.addEventListener('click', event => {
        if (performance.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, { capture: true, signal });
      this.track.addEventListener('pointerover', event => {
        const card = event.target.closest('[data-reel]');
        if (!card || card.contains(event.relatedTarget) || event.pointerType !== 'mouse' || this.dataset.hoverPlay !== 'true' || this.reduced.matches || this.dragState?.moved) return;
        this.hoveredCard = card;
        const video = card.querySelector('video');
        if (video) { video.muted = true; this.syncSound(video); this.playVideo(video, true); }
      }, { signal });
      this.track.addEventListener('pointerout', event => {
        const card = event.target.closest('[data-reel]');
        if (!card || card.contains(event.relatedTarget)) return;
        if (this.hoveredCard === card) this.hoveredCard = null;
        const video = card.querySelector('video');
        if (video?.dataset.hoverPlaying === 'true') this.pauseVideo(video);
      }, { signal });
      this.track.addEventListener('keydown', event => {
        if (!event.target.matches('[data-video-action]') || !['Enter',' '].includes(event.key)) return;
        event.preventDefault(); this.handleMedia({ target: event.target });
      }, { signal });
      this.track.addEventListener('wheel', () => { this.pauseUntil = performance.now() + 2000; }, { signal, passive: true });
      window.addEventListener('pointerup', () => { if (this.reasons.has('pointer')) this.pauseUntil = performance.now() + 2000; this.reasons.delete('pointer'); }, { signal });
      window.addEventListener('pointercancel', () => this.reasons.delete('pointer'), { signal });
      this.track.addEventListener('scroll', () => {
        this.lastInteraction = performance.now();
        this.normalize();
      }, { signal, passive: true });
      this.addEventListener('click', event => this.handleMedia(event), { signal });
      this.addEventListener('change', event => this.handleVariant(event), { signal });
      this.addEventListener('submit', event => this.handleAdd(event), { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.reasons.add('hidden');
          this.querySelectorAll('video').forEach(video => this.pauseVideo(video));
        } else this.reasons.delete('hidden');
      }, { signal });
      this.reduced.addEventListener('change', () => {
        if (this.reduced.matches) this.auto = false;
        this.syncMotionButton();
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        this.reasons.add('editor');
        const card = event.target.closest('[data-reel],.mvp-community__reason,.mvp-community__review');
        if (card) this.track.scrollLeft += card.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
      }, { signal });
      document.addEventListener('shopify:block:deselect', () => this.reasons.delete('editor'), { signal });
      this.resize = new ResizeObserver(() => this.layout());
      this.resize.observe(this.track);
      this.visibility = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        if (!this.inView) this.querySelectorAll('video').forEach(video => this.pauseVideo(video));
      });
      this.visibility.observe(this);
      this.layout();
      this.syncMotionButton();
      if (this.isReels) this.raf = requestAnimationFrame(now => this.tick(now));
    }
    layout() {
      if (!this.isReels || !this.originals.length) return;
      const signature = [this.track.clientWidth, ...this.originals.map(card => Math.round(card.getBoundingClientRect().width * 10))].join(':');
      // A resize callback must not replace an open product selector with a clone.
      if (signature === this.layoutSignature) return;
      if (this.reasons.has('hover') || this.reasons.has('focus') || this.reasons.has('pointer') || this.reasons.has('buying') || this.reasons.has('editor')) { this.layoutPending = true; return; }
      this.layoutPending = false; this.layoutSignature = signature;
      const oldCycle = this.cycle;
      const previousOffset = oldCycle ? ((this.track.scrollLeft % oldCycle) + oldCycle) % oldCycle : 0;
      this.cloneSets.flat().forEach(card => { card.querySelectorAll('video').forEach(video => video.pause()); card.remove(); });
      this.cloneSets = [];
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      this.cycle = this.originals.reduce((sum, card) => sum + card.getBoundingClientRect().width + gap, 0);
      if (!this.cycle) return;
      // One full pattern on either side; enough repeats to fill even a wide viewport.
      const copies = this.designMode ? 1 : Math.max(3, Math.ceil(this.track.clientWidth / this.cycle) + 3);
      for (let set = 1; set < copies; set++) {
        const clones = this.originals.map(card => {
          const clone = card.cloneNode(true);
          clone.removeAttribute('data-shopify-editor-block');
          clone.removeAttribute('data-shopify-editor-block-id');
          clone.dataset.reelClone = 'true';
          clone.querySelectorAll('[data-shopify-editor-block]').forEach(node => node.removeAttribute('data-shopify-editor-block'));
          clone.querySelectorAll('select,button').forEach(control => { control.disabled = control.matches('[type=submit]') ? Boolean(clone.querySelector('[data-variant]') && !clone.querySelector('[data-variant]').value) : control.disabled; });
          clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
          clone.querySelectorAll('video').forEach(video => {
            video.removeAttribute('src');
            delete video.dataset.loaded; delete video.dataset.hoverPlaying; video.dataset.playToken = '';
            video.setAttribute('aria-pressed', 'false');
            video.muted = true;
            video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
          });
          clone.querySelectorAll('[data-play]').forEach(button => { button.textContent = '▶'; button.setAttribute('aria-label', 'Reproducir video'); button.setAttribute('aria-pressed', 'false'); });
          clone.querySelectorAll('[data-mute]').forEach(button => { button.textContent = '♪̸'; button.setAttribute('aria-label', 'Activar sonido'); button.setAttribute('aria-pressed', 'false'); });
          clone.querySelectorAll('[data-product-status]').forEach(status => status.textContent = '');
          clone.querySelectorAll('[data-cart-link]').forEach(link => link.removeAttribute('data-visible'));
          this.track.append(clone);
          return clone;
        });
        this.cloneSets.push(clones);
      }
      this.track.scrollLeft = this.designMode ? previousOffset : this.cycle + previousOffset;
      this.ready = true;
      this.watchVideos();
    }
    normalize() {
      if (!this.ready || !this.cycle || this.designMode) return;
      if (this.track.contains(document.activeElement)) return;
      if (this.track.scrollLeft < this.cycle * .5) this.track.scrollLeft += this.cycle;
      else if (this.track.scrollLeft >= this.cycle * 1.5) this.track.scrollLeft -= this.cycle;
    }
    syncMotionButton() {
      if (!this.pauseButton) return;
      this.pauseButton.textContent = this.auto ? 'Ⅱ' : '▶';
      this.pauseButton.setAttribute('aria-label', this.auto ? 'Pausar carrusel' : 'Iniciar carrusel');
      this.pauseButton.setAttribute('aria-pressed', String(!this.auto));
    }
    tick(now) {
      const delta = this.lastFrame ? Math.min(now - this.lastFrame, 50) : 0;
      this.lastFrame = now;
      const playing = this.playingVideos.size > 0;
      if (this.layoutPending && !this.reasons.size && now > (this.pauseUntil || 0)) this.layout();
      if (this.auto && this.inView && !this.reasons.size && !playing && this.cycle && now > (this.pauseUntil || 0)) {
        this.motionCarry = (this.motionCarry || 0) + delta / 1000 * (Number(this.dataset.speed) || 18);
        const pixels = Math.floor(this.motionCarry);
        if (pixels) { this.track.scrollLeft += pixels; this.motionCarry -= pixels; this.normalize(); }
      }
      this.raf = requestAnimationFrame(next => this.tick(next));
    }
    step(direction) {
      if (!this.track.firstElementChild) return;
      this.pauseUntil = performance.now() + 2000;
      const distance = this.track.firstElementChild.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).gap) || 0);
      this.track.scrollLeft += distance * direction;
      this.normalize();
    }
    watchVideos() {
      this.videoObserver?.disconnect();
      this.videoObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.intersectionRatio < .35) this.pauseVideo(video);
        });
      }, { root: this.track, threshold: [0, .35] });
      this.querySelectorAll('video').forEach(video => {
        this.videoObserver.observe(video);
        if (this.dataset.showSound === 'false') video.muted = true;
        this.syncSound(video);
        video.onpause = () => this.setPlayState(video, false);
        video.onplay = () => this.setPlayState(video, true);
        video.onerror = () => {
          const status = video.closest('[data-reel]').querySelector('[data-product-status]');
          if (status) status.textContent = 'No se pudo reproducir el video. Intenta de nuevo.';
          this.setPlayState(video, false);
        };
      });
    }
    setPlayState(video, active) {
      if (active) this.playingVideos.add(video); else this.playingVideos.delete(video);
      video.setAttribute('aria-pressed', String(active));
      const button = video.closest('[data-reel]')?.querySelector('[data-play]');
      if (!button) return;
      button.textContent = active ? 'Ⅱ' : '▶';
      button.setAttribute('aria-label', active ? 'Pausar video' : 'Reproducir video');
      button.setAttribute('aria-pressed', String(active));
    }
    pauseVideo(video) { video.dataset.playToken = ''; delete video.dataset.hoverPlaying; video.pause(); this.setPlayState(video, false); }
    syncSound(video) {
      const button = video.closest('[data-reel]')?.querySelector('[data-mute]');
      if (!button) return;
      button.textContent = video.muted ? '♪̸' : '♪';
      button.setAttribute('aria-label', video.muted ? 'Activar sonido' : 'Silenciar video');
      button.setAttribute('aria-pressed', String(!video.muted));
    }
    async playVideo(video, hover = false) {
      const card = video.closest('[data-reel]');
      this.querySelectorAll('video').forEach(other => { if (other !== video) this.pauseVideo(other); });
      const token = video.dataset.playToken = String(++this.playSequence);
      if (hover) video.dataset.hoverPlaying = 'true'; else delete video.dataset.hoverPlaying;
      if (hover || this.dataset.showSound === 'false') video.muted = true;
      this.syncSound(video);
      if (!video.dataset.loaded) {
        video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
        video.load(); video.dataset.loaded = 'true';
      }
      try {
        await video.play();
        if (video.dataset.playToken !== token || !video.isConnected || (hover && this.hoveredCard !== card)) this.pauseVideo(video);
      } catch (_) {
        if (!hover && video.dataset.playToken === token) {
          const status = card.querySelector('[data-product-status]');
          if (status) status.textContent = 'Toca el video para intentar reproducirlo de nuevo.';
        }
        this.setPlayState(video, false);
      }
    }
    handleMedia(event) {
      const button = event.target.closest('[data-play],[data-mute]');
      const surface = event.target.closest('[data-video-action],.mvp-community__poster');
      if (!button && !surface) return;
      const card = event.target.closest('[data-reel]'), video = card?.querySelector('video');
      if (!video) return;
      if (button?.hasAttribute('data-mute')) {
        if (this.dataset.showSound === 'false') { video.muted = true; return; }
        video.muted = !video.muted; this.syncSound(video); return;
      }
      if (!video.paused) this.pauseVideo(video); else this.playVideo(video);
    }
    handleVariant(event) {
      const select = event.target.closest('[data-variant]');
      if (!select) return;
      const form = select.closest('form');
      const selected = select.selectedOptions[0];
      const price = form.closest('[data-reel]').querySelector('[data-price]');
      if (price && selected?.dataset.price) price.textContent = selected.dataset.price;
      const button = form.querySelector('[type="submit"]');
      if (!this.pendingForms.has(form)) button.disabled = !select.value;
      form.closest('[data-reel]').querySelector('[data-product-status]').textContent = '';
    }
    async handleAdd(event) {
      const form = event.target.closest('[data-buy]');
      if (!form) return;
      event.preventDefault();
      if (this.pendingForms.has(form) || !form.reportValidity()) return;
      const id = new FormData(form).get('id');
      if (!id) return;
      const card = form.closest('[data-reel]');
      const status = card.querySelector('[data-product-status]');
      const button = form.querySelector('[type="submit"]');
      const label = button.textContent;
      this.pendingForms.add(form); this.pendingCount++; this.reasons.add('buying');
      button.disabled = true;
      button.textContent = 'Agregando…';
      status.textContent = '';
      const root = window.Shopify?.routes?.root || '/';
      const drawer = document.querySelector('cart-drawer,cart-notification');
      const drawerSupported = drawer && typeof drawer.getSectionsToRender === 'function' && typeof drawer.renderContents === 'function';
      let sections = [];
      if (drawerSupported) {
        try { sections = drawer.getSectionsToRender().map(section => section.id).slice(0, 5); } catch (_) {}
      }
      let added = false;
      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: String(id), quantity: 1 }], ...(sections.length ? { sections, sections_url: window.location.pathname } : {}) })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw Object.assign(new Error(typeof result.description === 'string' ? result.description : 'No se pudo agregar. Revisa disponibilidad e intenta de nuevo.'), { cartRejected: true });
        added = true;
        status.textContent = '';
        if (drawerSupported && result.sections) {
          try { drawer.renderContents(result); } catch (_) { /* The item is already added; never repeat the request. */ }
        }
        document.dispatchEvent(new CustomEvent('mvp:cart:add', { detail: result }));
        fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then(r => r.ok ? r.json() : null).then(cart => {
          if (cart && Number.isFinite(cart.item_count)) document.querySelectorAll('[data-mvp-cart-count]').forEach(node => node.textContent = cart.item_count);
        }).catch(() => {});
      } catch (error) {
        if (!added) status.textContent = error.cartRejected ? error.message : 'No pudimos confirmar si se agregó. Revisa tu bolsa antes de volver a intentar.';
      } finally {
        this.pendingForms.delete(form); this.pendingCount--; if (!this.pendingCount) this.reasons.delete('buying');
        button.textContent = label;
        button.disabled = !form.elements.id?.value;
      }
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.resize?.disconnect(); this.visibility?.disconnect(); this.videoObserver?.disconnect();
      cancelAnimationFrame(this.raf);
      this.querySelectorAll('video').forEach(video => this.pauseVideo(video));
      this.cloneSets?.flat().forEach(card => card.remove());
    }
  }
  customElements.define('mvp-community-slider', MvpCommunitySlider);
})();
